import { COLLECTIONS } from '@/constants/collections';
import {
	type WorkerDetail,
	type WorkerListItem,
	WorkerType,
} from '@/modules/worker/types';

import type { LoadedWorker } from '@/components/dropdown/types';
import type { Contact } from '@/types/contact';

interface MappedWorkerToLoadedWorker
	extends Omit<WorkerListItem, 'jobNames' | 'manager' | 'delegable'> {
	jobNames?: { id: string; name: string }[];
	manager?: string;
	companies?: {
		refused: string[];
		approved: string[];
	};
}

export const mappedWorkerToLoadedWorker = (
	worker: MappedWorkerToLoadedWorker
): LoadedWorker => ({
	id: worker.id,
	firstname: worker.firstname ?? '',
	lastname: worker.lastname ?? '',
	jobNames: (worker.jobNames ?? []).map((job) => ({
		id: job.id,
		name: job.name,
	})),
	manager: worker.manager ?? '',
	phone: worker.phone ?? '',
	email: worker.email ?? '',
	city: worker.addressCity ?? '',
	vehicle: worker.vehicle,
	weekendShift: worker.weekendShift,
	nightShift: worker.nightShift,
	status: worker.mobileAppStatus ?? '',
	geolocated: worker.longitude != null && worker.latitude != null,
	inactiveByAgencies: '',
	askedChanges: '',
	agenciesList: [],
	createdAt: worker.createdAt ? new Date(worker.createdAt).getTime() : 0,
	picture: worker.picture ?? '',
	collection:
		worker.workerType === WorkerType.INTERIM
			? COLLECTIONS.USERS
			: COLLECTIONS.CANDIDATES,
	companiesBlacklist: worker.companies?.refused ?? [],
});

export const mappedWorkerDetailToLoadedWorker = (
	worker: WorkerDetail
): LoadedWorker => ({
	id: worker.id,
	firstname: worker.firstname ?? '',
	lastname: worker.lastname ?? '',
	jobNames: [],
	manager: '',
	phone: worker.phone ?? '',
	email: worker.email ?? '',
	city: worker.address?.city ?? '',
	vehicle: worker.vehicle,
	weekendShift: worker.weekendShift,
	nightShift: worker.nightShift,
	status: worker.mobileAppStatus ?? '',
	geolocated: true,
	inactiveByAgencies: '',
	askedChanges: '',
	agenciesList: worker.agencies?.map((agency) => agency.agencyId) ?? [],
	createdAt: worker.createdAt ? new Date(worker.createdAt).getTime() : 0,
	picture: worker.picture ?? '',
	collection:
		worker.workerType === WorkerType.INTERIM
			? COLLECTIONS.USERS
			: COLLECTIONS.CANDIDATES,
	companiesBlacklist: worker.companies?.refused ?? [],
});

export const contactToDropdownContact = (
	contacts: Contact[]
): (Contact & { label: string; value: string })[] => {
	return contacts
		.map((contact) => ({
			...contact,
			label:
				[contact.firstname || '', contact.lastname || '']
					.filter(Boolean)
					.join(' ') || '-',
			value: contact.id ?? '',
		}))
		.sort((a, b) => a.label.localeCompare(b.label));
};
